/**
 * Under a board item: its AI summary, and a comment that goes straight onto
 * the item's issue, signed with the writer's name.
 */

import { useState } from 'react'
import { Button, Textarea, makeStyles, tokens } from '@fluentui/react-components'
import { Comment16Regular } from '@fluentui/react-icons'
import { useToast } from '../ToastProvider'
import { usePostIssueComment } from '../../hooks/usePlanning'
import { apiErrorMessage } from '../../lib/apiError'

export function isAxiosStatus(err: unknown, status: number): boolean {
  return (err as { response?: { status?: number } } | null)?.response?.status === status
}

const useStyles = makeStyles({
  note: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  sum: {
    margin: 0,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground2,
  },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS, justifyContent: 'flex-end' },
})

export function IssueNote({
  cycle,
  issue,
  title,
  sum,
  numbered = false,
}: {
  cycle: string
  issue: number
  title: string
  sum?: string
  /** On an item with several issues: say whose summary this is. */
  numbered?: boolean
}) {
  const styles = useStyles()
  const { showToast } = useToast()
  const post = usePostIssueComment(cycle)
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const close = () => {
    setOpen(false)
    setText('')
  }
  const send = () => {
    const body = text.trim()
    if (!body) return
    post.mutate(
      { issue, text: body },
      {
        onSuccess: ({ url }) => {
          close()
          showToast({
            title: `Posted to #${issue}`,
            intent: 'success',
            action: { label: 'Open on GitHub', onClick: () => window.open(url, '_blank', 'noopener') },
          })
        },
        onError: (err) =>
          showToast({
            // 504: the request reached GitHub and no answer came back.
            title: isAxiosStatus(err, 504) ? 'Not confirmed' : 'Not posted',
            body: apiErrorMessage(err),
            intent: isAxiosStatus(err, 504) ? 'warning' : 'error',
          }),
      },
    )
  }
  return (
    <div className={styles.note}>
      {sum && (
        <p className={styles.sum} data-testid={`sum-${issue}`}>
          {numbered && <b>#{issue} </b>}
          {sum}
        </p>
      )}
      {open ? (
        <>
          {/* Held while it posts: the answer closes the box, and text typed
              meanwhile would go with it unsent (Copilot on #1620). */}
          <Textarea
            size="small"
            resize="vertical"
            value={text}
            disabled={post.isPending}
            aria-label={`Comment on #${issue}`}
            placeholder="Goes onto the issue, signed with your name"
            onChange={(_, d) => setText(d.value)}
          />
          <div className={styles.actions}>
            <Button size="small" appearance="subtle" disabled={post.isPending} onClick={close}>
              Cancel
            </Button>
            <Button
              size="small"
              appearance="primary"
              disabled={post.isPending || !text.trim()}
              onClick={send}
            >
              Post to #{issue}
            </Button>
          </div>
        </>
      ) : (
        <div>
          <Button
            size="small"
            appearance="subtle"
            icon={<Comment16Regular />}
            aria-label={`Comment on ${title}`}
            onClick={() => setOpen(true)}
          >
            Comment
          </Button>
        </div>
      )}
    </div>
  )
}
